import {PaymentModel} from "../payment.model";
import {ParamConfig} from "../../config-support/param-config.service";
import {
    PaymentAuthorisationType,
    WalletPaymentErrorType,
    WalletPaymentSubmissionFailure,
    WalletPaymentSubmissionResult,
    WalletProvider
} from "../payment";
import {TyroPayRequestError} from "../tyro";
import {WalletPaymentProcessor} from "./wallet-payment-processor";

export class TyroWalletPaymentProcessor extends WalletPaymentProcessor {
    private tyro: any;
    private payForm: any;
    private payFormHolder: HTMLElement;

    constructor(paymentModel: PaymentModel) {
        super(paymentModel);
    }

    public async init(): Promise<WalletPaymentProcessor> {
        this.quickPayAvailable = false;
        if (!this.paymentModel.paymentPlatformPublicKey) {
            return undefined;
        }

        console.log("Initialising Tyro for wallet payment. Live Mode =", this.paymentModel.paymentPlatformLiveMode);
        try {
            this.tyro = (<any>window).Tyro({
                liveMode: this.paymentModel.paymentPlatformLiveMode
            });
            await this.tyro.init(this.paymentModel.paymentPlatformPublicKey);
            this.payForm = this.tyro.createPayForm({
                options: {
                    applePay: {
                        enabled: true
                    },
                    googlePay: {
                        enabled: true,
                        merchantInfo: {
                            merchantName: this.paymentModel.sessionModel.currentApp.googlePayMerchantName,
                            merchantId: this.paymentModel.paymentPlatformLiveMode ? this.paymentModel.sessionModel.currentApp.googlePayMerchantId : undefined
                        }
                    },
                    creditCardForm: {
                        enabled: false
                    }
                }
            });

            // Inject the pay form into a "dummy" element.
            if (!this.payFormHolder) {
                const oldPayFormHolder: HTMLElement = document.querySelector("#tyro-wallet-pay-form-holder");
                if (oldPayFormHolder) {
                    document.body.removeChild(oldPayFormHolder);
                }
                this.payFormHolder = document.createElement("div");
                this.payFormHolder.id = "tyro-wallet-pay-form-holder";
                this.payFormHolder.style.display = "none";
                document.body.appendChild(this.payFormHolder);
            }
            if (this.payFormHolder.firstChild) {
                this.payFormHolder.removeChild(this.payFormHolder.firstChild);
            }
            await this.payForm.inject("#tyro-wallet-pay-form-holder");
        } catch (error) {
            console.error("Tyro wallet payment initialisation failed", error);
            return undefined;
        }

        const paramConfig: ParamConfig = await this.paymentModel.paramConfigService.getConfig();
        this.walletProvider = paramConfig.forcePay === "apple" ? WalletProvider.APPLE_PAY
            : paramConfig.forcePay === "google" ? WalletProvider.GOOGLE_PAY
                : undefined;
        this.canSubmit = !paramConfig.forceTyroWalletButtons;

        if (this.walletProvider === undefined) {

            // Tyro has no mechanism to detect whether wallet payment is configured on the device.
            // Use the Stripe API for this.
            let availability: any;
            if (this.paymentModel.sessionModel.currentApp.stripeTestPublicKey) {
                try {
                    availability = await new (<any>window).Stripe(this.paymentModel.sessionModel.currentApp.stripeTestPublicKey).paymentRequest({
                        country: "AU",
                        currency: "aud",
                        total: {
                            label: "Order total",
                            amount: 100
                        },
                    }).canMakePayment();
                    if (availability) {
                        this.walletProvider = availability.applePay ? WalletProvider.APPLE_PAY
                            : availability.googlePay ? WalletProvider.GOOGLE_PAY
                                : undefined;
                    }
                } catch (error) {
                    console.error("Failed to check wallet availability using Stripe", error);
                }
            }

            // Fall back to checking with Tyro (only checks the device capability).
            if (!availability) {
                try {
                    this.walletProvider = this.payForm.isApplePaySupported() ? WalletProvider.APPLE_PAY
                        : this.payForm.isGooglePaySupported() ? WalletProvider.GOOGLE_PAY
                            : undefined;
                } catch (error) {
                    console.error("Failed to check wallet availability using Tyro", error);
                }
            }
            console.log("Wallet payment provider =", this.walletProvider);
            if (this.walletProvider == undefined) {
                return undefined;
            }

            if (this.canSubmit && (
                (this.walletProvider === WalletProvider.APPLE_PAY && !this.payForm.applePayButton.onApplePayButtonClicked) ||
                (this.walletProvider === WalletProvider.GOOGLE_PAY && !this.payForm.applePayButton.onApplePayButtonClicked))) {
                console.log("Using Tyro wallet payment buttons - API version is incompatible with passive button mode.");
                this.canSubmit = false;
            }
        }

        // noinspection JSUnusedLocalSymbols
        this.payForm.setWalletPaymentCompleteListener(async (paymentType: string, error: TyroPayRequestError) => {
            if (error) {
                this.rejectSubmission(new WalletPaymentSubmissionFailure(WalletPaymentErrorType.PROVIDER_ERROR, error.type, error.errorMessage, error.errorCode, error.gatewayCode));
                return;
            }

            // noinspection ES6MissingAwait
            this.paymentModel.analyticsService.trackWalletPaymentRequestSucceeded(this.walletProvider);
            this.resolveSubmission(new WalletPaymentSubmissionResult(PaymentAuthorisationType.TYRO_PAYMENT_INTENT));
            this.rejectSubmission = undefined;
            this.resolveSubmission = undefined;
        });

        // noinspection JSUnusedLocalSymbols
        this.payForm.setWalletPaymentCancelledListener((paymentType: string) => {
            this.paymentModel.analyticsService.trackWalletPaymentRequestCancelled(this.walletProvider);
            this.rejectSubmission(new WalletPaymentSubmissionFailure(this.walletProvider === WalletProvider.APPLE_PAY ? WalletPaymentErrorType.APPLE_PAY_CANCELLED : WalletPaymentErrorType.GOOGLE_PAY_CANCELLED));
            this.rejectSubmission = undefined;
            this.resolveSubmission = undefined;
        });
        return this;
    }

    // noinspection JSUnusedLocalSymbols
    public async submit(amount: number): Promise<WalletPaymentSubmissionResult> {
        if (!this.canSubmit) {
            throw new WalletPaymentSubmissionFailure(WalletPaymentErrorType.INVALID_STATE);
        }

        // noinspection ES6MissingAwait
        this.paymentModel.analyticsService.trackWalletPaymentRequestStarted(this.walletProvider);

        if (this.resolveSubmission) {
            this.rejectSubmission(new WalletPaymentSubmissionFailure(WalletPaymentErrorType.INVALID_STATE));
            await this.init();
        }

        return new Promise<WalletPaymentSubmissionResult>((resolve: (value?: WalletPaymentSubmissionResult | PromiseLike<WalletPaymentSubmissionResult>) => void, reject: (reason?: any) => void) => {
            this.resolveSubmission = resolve;
            this.rejectSubmission = reject;

            // payForm.googlePayButton.onGooglePayButtonClicked and
            // payForm.applePayButton.onApplePayButtonClicked are undocumented APIs that allow us
            // to handle Tyro wallet payments the same way we do for Stripe.
            // i.e. with a passive button to start the order placement flow, and invocation of the wallet payment
            // only after validations and other async processing has completed.
            if (this.walletProvider === WalletProvider.APPLE_PAY) {
                this.payForm.applePayButton.onApplePayButtonClicked();
            } else if (this.walletProvider === WalletProvider.GOOGLE_PAY) {
                this.payForm.googlePayButton.onGooglePayButtonClicked();
            }
        });
    }
}


// WEBPACK FOOTER //
// ./src/app/payment/wallet-payment/tyro-wallet-payment-processor.ts